import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Building2, ArrowRight, Shield, Layers, BarChart3, CheckCircle } from 'lucide-react';

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    navigate('/dashboard');
    return null;
  }

  const features = [
    {
      icon: <Layers className="w-6 h-6" />,
      title: 'Virtual Departments',
      description: 'Create independent cost centers with unique account numbers and UPI IDs'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Rule-Based Governance',
      description: 'Set spend limits, allocations, and custom rules per department'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Complete Transparency',
      description: 'Full audit trails and real-time reporting across all transactions'
    }
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white to-slate-50/30" />
      
      {/* Navbar */}
      <nav className="relative z-10 px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/20">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-gray-900 font-['Outfit']">FinOrchest</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/security">
            <Button variant="ghost" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100">
              Sign In
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-blue-900 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/20">
              Get Started
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 mb-8">
            <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
            <span className="text-sm text-blue-800 font-medium">Decentralized Fund Management</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 font-['Outfit'] tracking-tight">
            Orchestrate Your
            <span className="text-blue-900"> Organizational</span>
            <br />Finance
          </h1>
          
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Enable departments to operate independently while maintaining complete control.
            Virtual accounts, rule-based governance, and full transparency.
          </p>
          
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-blue-900 hover:bg-blue-800 text-white gap-2 px-8 h-12 text-lg shadow-lg shadow-blue-900/20">
                Start Free <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 h-12 px-8 text-lg">
                Sign In
              </Button>
            </Link>
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6 mt-24">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="glass-card p-8 animate-fadeInUp hover:shadow-lg transition-shadow duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-900 flex items-center justify-center text-white mb-4 shadow-lg shadow-blue-900/20">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 font-['Outfit']">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mt-32">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4 font-['Outfit']">How It Works</h2>
          <p className="text-gray-600 text-center mb-12 max-w-xl mx-auto">
            Get started in minutes with our streamlined setup process
          </p>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Link Bank Account', desc: 'Connect your main organizational bank account' },
              { step: '02', title: 'Create Departments', desc: 'Set up virtual cost centers with unique identifiers' },
              { step: '03', title: 'Define Rules', desc: 'Configure spend limits and approval workflows' },
              { step: '04', title: 'Start Transacting', desc: 'Department heads can initiate payments independently' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 rounded-xl hover:bg-gray-50 transition-colors duration-200">
                <div className="text-4xl font-bold text-blue-900/30 mb-4 font-['JetBrains_Mono']">{item.step}</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-gray-200 py-8 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-gray-500 text-sm">© 2026 FinOrchest. All rights reserved.</p>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-gray-600">Bank-grade security</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
